import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Copy,
  CheckCircle2,
  Loader2,
  Crown,
  ArrowRight,
  MousePointerClick,
  Users,
  TrendingUp,
  Wallet,
  LogOut,
  Pencil,
  Info,
  Mail,
  Share2,
} from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'levanta_aff_session';

const fmtMoney = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtMonth = (year, month) =>
  new Date(year, month - 1).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });

const StatCard = ({ Icon, label, value, sub, accent = 'blue' }) => {
  const accents = {
    blue: 'from-blue-500 to-cyan-400',
    emerald: 'from-emerald-500 to-teal-500',
    amber: 'from-amber-500 to-orange-500',
    violet: 'from-violet-500 to-purple-500',
  };
  return (
    <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5 md:p-6">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accents[accent]} text-white flex items-center justify-center shadow-md mb-4`}>
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0b1f3a]/55 mb-1">{label}</p>
      <p className="text-2xl md:text-3xl font-black text-[#0b1f3a] tracking-tight">{value}</p>
      {sub && <p className="text-xs text-[#0b1f3a]/55 mt-1">{sub}</p>}
    </div>
  );
};

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');
  const [editingPayout, setEditingPayout] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState('paypal');
  const [payoutAddress, setPayoutAddress] = useState('');
  const [savingPayout, setSavingPayout] = useState(false);

  // Pick up the session token from the URL fragment (set by /api/affiliate/login/verify),
  // store in localStorage, then strip it from the URL.
  useEffect(() => {
    const hash = window.location.hash;
    let token = null;
    if (hash && hash.startsWith('#session=')) {
      token = hash.slice('#session='.length);
      try {
        window.localStorage.setItem(SESSION_KEY, token);
      } catch (_) {
        /* no-op */
      }
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (!token) {
      try {
        token = window.localStorage.getItem(SESSION_KEY);
      } catch (_) {
        token = null;
      }
    }
    if (!token) {
      navigate('/affiliates/login');
      return;
    }
    setSession(token);
  }, [navigate]);

  const fetchDashboard = useCallback(async (token) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliate/me/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        try {
          window.localStorage.removeItem(SESSION_KEY);
        } catch (_) {
          /* no-op */
        }
        navigate('/affiliates/login?err=expired');
        return;
      }
      const json = await res.json();
      setData(json);
      setPayoutMethod(json?.payout_info?.method || 'paypal');
      setPayoutAddress(json?.payout_info?.address || '');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (session) fetchDashboard(session);
  }, [session, fetchDashboard]);

  const copy = (key, value) => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(''), 1800);
    } catch (_) {
      /* no-op */
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/affiliate/me/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session}` },
      });
    } catch (_) {
      /* no-op */
    }
    try {
      window.localStorage.removeItem(SESSION_KEY);
    } catch (_) {
      /* no-op */
    }
    navigate('/affiliates/login');
  };

  const savePayout = async () => {
    setSavingPayout(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliate/me/payout-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session}`,
        },
        body: JSON.stringify({ method: payoutMethod, address: payoutAddress }),
      });
      if (res.ok) {
        setEditingPayout(false);
        await fetchDashboard(session);
      }
    } finally {
      setSavingPayout(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="bg-[#f4f8fc] min-h-screen overflow-x-hidden">
        <AltHeader />
        <div className="pt-[140px] pb-32 flex flex-col items-center justify-center text-[#0b1f3a]/60">
          <Loader2 className="w-7 h-7 animate-spin mb-3" />
          <p className="text-sm">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  const { affiliate, stats, commission_rates, recent_referrals, monthly_history, payout_info, disclaimer } = data;
  const maxHistory = Math.max(1, ...monthly_history.map((h) => h.earnings_cents));

  return (
    <div className="bg-[#f4f8fc] min-h-screen overflow-x-hidden">
      <AltHeader />

      <main className="pt-[110px] md:pt-[170px] pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* HERO */}
          <div className="rounded-3xl bg-gradient-to-br from-[#0b1f3a] via-[#0f2a4d] to-[#0b1f3a] text-white p-6 md:p-10 relative overflow-hidden mb-8">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-cyan-400/10 rounded-full blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-2">
                  Welcome back
                </p>
                <h1 className="text-2xl md:text-4xl font-black tracking-tight mb-1">
                  {affiliate.full_name}
                </h1>
                <p className="text-white/65 text-sm md:text-base">{affiliate.email}</p>
                {affiliate.is_founding_50 && (
                  <span className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[11px] font-bold tracking-[0.18em] uppercase shadow-md">
                    <Crown className="w-3.5 h-3.5" strokeWidth={2.8} />
                    Founding Partner
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                data-testid="affiliate-dashboard-logout"
                className="self-start md:self-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 ring-1 ring-white/20 text-sm font-semibold hover:bg-white/15 transition"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>

            {/* Code + share link */}
            <div className="relative mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-md p-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2">Your code</p>
                <p className="font-mono text-3xl md:text-4xl font-black tracking-[0.12em] mb-3 break-all">{affiliate.code}</p>
                <button
                  type="button"
                  onClick={() => copy('code', affiliate.code)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0b1f3a] text-xs font-bold uppercase tracking-wider hover:shadow-lg transition"
                >
                  {copied === 'code' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'code' ? 'Copied' : 'Copy code'}
                </button>
              </div>
              <div className="rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-md p-5 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2">Share link</p>
                <p className="text-sm md:text-base text-white/85 mb-3 break-all">{affiliate.share_link}</p>
                <button
                  type="button"
                  onClick={() => copy('link', affiliate.share_link)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0b1f3a] text-xs font-bold uppercase tracking-wider hover:shadow-lg transition"
                >
                  {copied === 'link' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5" />}
                  {copied === 'link' ? 'Copied' : 'Copy link'}
                </button>
              </div>
            </div>
          </div>

          {/* STATS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard Icon={MousePointerClick} label="Clicks total" value={stats.clicks_total.toLocaleString()} sub={`${stats.clicks_this_month} this month`} accent="blue" />
            <StatCard Icon={Users} label="Active referrals" value={stats.active_referrals} sub={`${stats.referrals_total} all time`} accent="emerald" />
            <StatCard Icon={TrendingUp} label="This month" value={fmtMoney(stats.earnings_this_month_cents)} sub="Confirmed first-month commissions" accent="amber" />
            <StatCard Icon={Wallet} label="Pending payout" value={fmtMoney(stats.pending_payout_cents)} sub={`Next on ${stats.next_payout_date}`} accent="violet" />
          </div>

          {/* PROJECTED + DISCLAIMER */}
          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-6 md:p-8 mb-8">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">Projected — next 12 months</p>
                <p className="text-3xl md:text-4xl font-black tracking-tight text-[#0b1f3a] mb-2">
                  {fmtMoney(stats.projected_next_12_months_cents)}
                </p>
                <p className="text-sm text-[#0b1f3a]/65 leading-relaxed max-w-2xl">
                  {disclaimer}
                </p>
              </div>
            </div>
          </div>

          {/* MONTHLY CHART */}
          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-6 md:p-8 mb-8">
            <h3 className="text-lg md:text-xl font-black text-[#0b1f3a] mb-1">Earnings — last 12 months</h3>
            <p className="text-sm text-[#0b1f3a]/60 mb-6">First-month commissions on confirmed referrals.</p>
            <div className="flex items-end gap-1.5 md:gap-2 h-32 overflow-x-auto">
              {[...monthly_history].reverse().map((h) => {
                const heightPct = Math.max(4, (h.earnings_cents / maxHistory) * 100);
                return (
                  <div key={`${h.year}-${h.month}`} className="flex-1 min-w-[28px] flex flex-col items-center gap-1.5">
                    <div className="w-full bg-blue-500/15 rounded-t-md relative" style={{ height: `${heightPct}%` }}>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-blue-600 to-cyan-500 rounded-t-md" style={{ height: '100%' }} />
                    </div>
                    <p className="text-[10px] text-[#0b1f3a]/45 font-mono uppercase">{fmtMonth(h.year, h.month).split(' ')[0]}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RECENT REFERRALS */}
          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-6 md:p-8 mb-8">
            <h3 className="text-lg md:text-xl font-black text-[#0b1f3a] mb-1">Recent referrals</h3>
            <p className="text-sm text-[#0b1f3a]/60 mb-6">Last 20 sign-ups using your code. Email is masked for privacy.</p>
            {recent_referrals.length === 0 ? (
              <p className="text-sm text-[#0b1f3a]/55 py-6 text-center">No referrals yet — share your code to get started.</p>
            ) : (
              <div className="overflow-x-auto -mx-6 md:mx-0">
                <table className="w-full min-w-[540px] text-sm">
                  <thead>
                    <tr className="text-[#0b1f3a]/55 border-b border-black/5">
                      <th className="px-6 md:px-3 py-3 text-left font-semibold uppercase tracking-wider text-[11px]">User</th>
                      <th className="py-3 text-left font-semibold uppercase tracking-wider text-[11px]">Joined</th>
                      <th className="py-3 text-left font-semibold uppercase tracking-wider text-[11px]">Status</th>
                      <th className="px-6 md:px-3 py-3 text-right font-semibold uppercase tracking-wider text-[11px]">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent_referrals.map((r, i) => (
                      <tr key={i} className="border-b border-black/5 last:border-0">
                        <td className="px-6 md:px-3 py-3 font-mono text-[#0b1f3a]/75">{r.user_id_masked}</td>
                        <td className="py-3 text-[#0b1f3a]/75">{r.joined_at}</td>
                        <td className="py-3">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                            <CheckCircle2 className="w-3 h-3" strokeWidth={3} />
                            Active
                          </span>
                        </td>
                        <td className="px-6 md:px-3 py-3 text-right font-bold text-[#0b1f3a]">{fmtMoney(r.first_month_commission_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* COMMISSION RATES + PAYOUT */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-6 md:p-8">
              <h3 className="text-lg font-black text-[#0b1f3a] mb-4">
                Your commission rates
                {affiliate.is_founding_50 && (
                  <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold tracking-wider uppercase align-middle">
                    <Crown className="w-3 h-3" strokeWidth={3} /> Founding
                  </span>
                )}
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#0b1f3a]/65">First month</span>
                  <span className="font-bold text-[#0b1f3a]">{commission_rates.first_month_pct}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#0b1f3a]/65">Recurring</span>
                  <span className="font-bold text-[#0b1f3a]">{commission_rates.recurring_pct}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#0b1f3a]/65">Annual plan</span>
                  <span className="font-bold text-[#0b1f3a]">{commission_rates.annual_pct}%</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-black/5">
                  <span className="text-[#0b1f3a]/65">Recurring cap</span>
                  <span className="font-bold text-[#0b1f3a]">
                    {commission_rates.recurring_cap_months ? `${commission_rates.recurring_cap_months} months` : 'Lifetime'}
                  </span>
                </div>
              </div>
            </div>
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-[#0b1f3a]">Payout info</h3>
                {!editingPayout && (
                  <button
                    type="button"
                    onClick={() => setEditingPayout(true)}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-semibold hover:underline"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>
              {editingPayout ? (
                <div className="space-y-4">
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0b1f3a]/55 mb-2 block">Method</span>
                    <select
                      value={payoutMethod}
                      onChange={(e) => setPayoutMethod(e.target.value)}
                      className="w-full bg-[#fafbfd] text-[#0b1f3a] px-4 py-3 rounded-xl ring-1 ring-black/10 focus:ring-blue-500 outline-none transition"
                    >
                      <option value="paypal">PayPal</option>
                      <option value="bank">Bank transfer</option>
                    </select>
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0b1f3a]/55 mb-2 block">
                      {payoutMethod === 'paypal' ? 'PayPal email' : 'Bank account / IBAN'}
                    </span>
                    <input
                      type="text"
                      value={payoutAddress}
                      onChange={(e) => setPayoutAddress(e.target.value)}
                      placeholder={payoutMethod === 'paypal' ? 'you@example.com' : 'IBAN or account #'}
                      className="w-full bg-[#fafbfd] text-[#0b1f3a] px-4 py-3 rounded-xl ring-1 ring-black/10 focus:ring-blue-500 outline-none transition"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={savePayout}
                      disabled={savingPayout || !payoutAddress.trim()}
                      className="flex-1 px-5 py-2.5 rounded-full bg-[#0b1f3a] text-white text-sm font-semibold disabled:opacity-50 hover:shadow-lg transition"
                    >
                      {savingPayout ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingPayout(false)}
                      className="px-5 py-2.5 rounded-full bg-[#f4f8fc] text-[#0b1f3a] text-sm font-semibold hover:bg-black/5 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#0b1f3a]/65">Method</span>
                    <span className="font-bold text-[#0b1f3a] capitalize">
                      {payout_info.method || <span className="font-medium italic text-[#0b1f3a]/45">Not set</span>}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4 min-w-0">
                    <span className="text-[#0b1f3a]/65 shrink-0">Address</span>
                    <span className="font-bold text-[#0b1f3a] truncate min-w-0 text-right">
                      {payout_info.address || <span className="font-medium italic text-[#0b1f3a]/45">Not set</span>}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* HELP */}
          <div className="rounded-3xl bg-[#0b1f3a] text-white p-6 md:p-8 flex items-start gap-4">
            <Mail className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="text-base md:text-lg font-black mb-1">Questions?</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Write to <a href="mailto:business@levanta.life" className="text-blue-300 underline hover:text-white">business@levanta.life</a> — we answer within one business day. Read the full{' '}
                <Link to="/affiliate/terms" className="text-blue-300 underline hover:text-white">Affiliate Program terms</Link>.
              </p>
            </div>
          </div>
        </div>
      </main>

      <AltFooter />
    </div>
  );
};

export default AffiliateDashboard;
